/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Table, type TablePaginationConfig, type TableProps } from 'antd';
import {
  ISearchTaxInvoiceReportReq,
  ISearchTaxInvoiceReportRes,
  ITaxInvoice,
} from '@/interfaces/report';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { sumBy } from 'lodash';
import { getOrderStatusColor, getOrderStatusName } from '@/helpers/orderHelper';
import { twMerge } from 'tailwind-merge';

interface IProps {
  loading: boolean;
  criteria: ISearchTaxInvoiceReportReq;
  report: ISearchTaxInvoiceReportRes;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ITaxInvoice> | SorterResult<ITaxInvoice>[],
  ) => void;
}

const SalesReportTable = ({
  loading,
  criteria,
  report,
  onTableChange,
}: IProps) => {
  const columns: TableProps<ITaxInvoice>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <div>{i + 1}</div>,
    },
    {
      sorter: true,
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: _ => {
        const name = getOrderStatusName(_);
        const status = getOrderStatusColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5 px-1',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      title: 'วันที่',
      dataIndex: 'orderDate',
      key: 'orderDate',
      className: 'font-bold',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'BU',
      dataIndex: 'buName',
      key: 'buName',
      width: 100,
      align: 'center',
      className: 'font-bold',
    },
    {
      title: 'เลขที่เอกสาร',
      dataIndex: 'orderNo',
      key: 'orderNo',
      align: 'center',
      width: 140,
      className: 'font-bold',
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: 'เลขประจำตัวผู้เสียภาษี',
      dataIndex: 'customerTaxId',
      key: 'customerTaxId',
      className: 'font-bold',
      width: 160,
      align: 'center',
    },
    {
      title: 'สถานประกอบการ',
      dataIndex: 'branchName',
      key: 'branchName',
      className: 'font-bold',
      width: 140,
      align: 'center',
    },
    {
      title: 'มูลค่าสินค้า',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      className: 'font-bold text-green-700',
      align: 'right',
      width: 140,
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ภาษี',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      className: 'font-bold text-blue-700',
      align: 'right',
      width: 140,
      render: (_: number) => formatNumber(_, 2),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ITaxInvoice>
        columns={columns}
        criteria={criteria}
        data={report}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1300}
        scroll
        showPagination={false}
        summary={(data: ITaxInvoice[]) => {
          const totalAmount = sumBy(data || [], d => d.totalAmount);
          const totalTaxAmount = sumBy(data || [], d => d.vatAmount);
          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell
                  index={0}
                  colSpan={8}
                  className="font-bold items-end"
                  align="right"
                >
                  รวม
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={7}
                  colSpan={1}
                  className="font-bold items-end bg-green-100"
                  align="right"
                >
                  {formatNumber(totalAmount, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  colSpan={1}
                  className="font-bold items-end bg-blue-100"
                  align="right"
                >
                  {formatNumber(totalTaxAmount, 2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        sticky={{ offsetHeader: 65 }}
      />
    </div>
  );
};

export default SalesReportTable;
